import {
  AnimationClip,
  type Bone,
  QuaternionKeyframeTrack,
  Quaternion,
  type Skeleton,
  Vector3
} from 'three'
import { IkSolver } from '../../lib/processes/build-skeleton/IkSolver'

/**
 * FootLockProcessor
 *
 * Post-process a recorded mocap clip to eliminate foot-skate: the most common
 * artifact where a planted foot drifts horizontally because the body's root
 * motion drives faster (or slower) than the foot's actual world velocity.
 *
 * Algorithm:
 *  1. Walk every keyframe time in the clip and sample each foot's world
 *     position by applying the clip's tracks to a working skeleton.
 *  2. Detect "plant phases" where each foot's world Y is below a ground
 *     threshold AND its vertical velocity is below a stillness threshold.
 *  3. For each plant phase, pick an anchor world position (median across
 *     the phase). For each frame in the phase, run the existing CCD IK
 *     solver on the leg chain (calf + thigh by default) to keep the foot
 *     at the anchor while the rest of the body moves normally.
 *  4. Replace the affected leg bones' QuaternionKeyframeTracks with the
 *     adjusted rotations.
 *
 * Other tracks (root motion, upper body, opposite leg) pass through
 * untouched. The output clip has the same duration and frame timing.
 */

export interface FootLockOptions {
  /** Foot bone names to consider. Defaults to `foot_l` + `foot_r`. */
  foot_bone_names?: string[]
  /** World Y threshold below which the foot is considered "near ground". Default 0.05m. */
  ground_threshold_m?: number
  /** Vertical velocity threshold below which the foot is "still". Default 0.15 m/s. */
  velocity_threshold_mps?: number
  /** Number of parent bones the IK solver bends. Default 2 (calf + thigh). */
  ik_chain_length?: number
  /** Minimum number of contiguous frames to qualify as a plant phase. Default 3. */
  min_plant_frames?: number
}

export interface FootLockResult {
  clip: AnimationClip
  plant_phases_per_foot: Record<string, number>
  frames_adjusted: number
}

interface PlantPhase {
  start_index: number
  end_index: number  // inclusive
  anchor_world: Vector3
}

export class FootLockProcessor {
  static apply (clip: AnimationClip, skeleton: Skeleton, options: FootLockOptions = {}): FootLockResult {
    const foot_bone_names = options.foot_bone_names ?? ['foot_l', 'foot_r']
    const ground_threshold = options.ground_threshold_m ?? 0.05
    const velocity_threshold = options.velocity_threshold_mps ?? 0.15
    const chain_length = options.ik_chain_length ?? 2
    const min_plant_frames = options.min_plant_frames ?? 3

    // Build the union of keyframe times across all tracks. The processor
    // works on this discrete frame set — no resampling.
    const time_set = new Set<number>()
    for (const track of clip.tracks) {
      for (let i = 0; i < track.times.length; i++) time_set.add(track.times[i])
    }
    const times = Array.from(time_set).sort((a, b) => a - b)
    if (times.length < 2) {
      return { clip, plant_phases_per_foot: {}, frames_adjusted: 0 }
    }

    // Per-bone track index map for fast lookup + modification.
    const track_by_name = new Map<string, QuaternionKeyframeTrack>()
    for (const track of clip.tracks) {
      if (track instanceof QuaternionKeyframeTrack) {
        const bone_name = track.name.split('.')[0]
        track_by_name.set(bone_name, track)
      }
    }

    const bone_by_name = new Map<string, Bone>()
    for (const bone of skeleton.bones) bone_by_name.set(bone.name, bone)

    const plant_phases_per_foot: Record<string, number> = {}
    const adjusted_bone_names = new Set<string>()
    let frames_adjusted = 0

    for (const foot_name of foot_bone_names) {
      const foot_bone = bone_by_name.get(foot_name)
      if (foot_bone === undefined) continue

      // Phase 1: sample foot world Y at every keyframe time.
      const foot_y_per_frame: number[] = new Array(times.length)
      const foot_world_per_frame: Vector3[] = new Array(times.length)
      for (let i = 0; i < times.length; i++) {
        apply_pose_at_time(skeleton, clip, times[i])
        const wp = new Vector3()
        foot_bone.getWorldPosition(wp)
        foot_y_per_frame[i] = wp.y
        foot_world_per_frame[i] = wp
      }

      // Phase 2: detect plant phases.
      const phases = detect_plant_phases(
        times, foot_y_per_frame, foot_world_per_frame,
        ground_threshold, velocity_threshold, min_plant_frames
      )
      plant_phases_per_foot[foot_name] = phases.length

      if (phases.length === 0) continue

      // Phase 3: for each phase frame, run IK to keep foot at anchor.
      // Collect (time, new_quaternion) for every chain bone we touched.
      const updates_per_chain_bone = new Map<string, Map<number, [number, number, number, number]>>()

      for (const phase of phases) {
        for (let frame_i = phase.start_index; frame_i <= phase.end_index; frame_i++) {
          const t = times[frame_i]
          apply_pose_at_time(skeleton, clip, t)
          IkSolver.solve_ccd(foot_bone, chain_length, phase.anchor_world, {
            iterations: 12, damping: 0.9
          })
          // Walk up the chain and record new local quaternions.
          let cursor: Bone | null = foot_bone.parent instanceof Object &&
            (foot_bone.parent as Bone).isBone === true ? foot_bone.parent as Bone : null
          let walked = 0
          while (cursor !== null && walked < chain_length) {
            const map = updates_per_chain_bone.get(cursor.name) ?? new Map<number, [number, number, number, number]>()
            map.set(t, [cursor.quaternion.x, cursor.quaternion.y, cursor.quaternion.z, cursor.quaternion.w])
            updates_per_chain_bone.set(cursor.name, map)
            adjusted_bone_names.add(cursor.name)
            walked++
            cursor = cursor.parent instanceof Object && (cursor.parent as Bone).isBone === true ? cursor.parent as Bone : null
          }
          frames_adjusted++
        }
      }

      // Phase 4: write the adjusted rotations back into the affected tracks.
      for (const [bone_name, updates] of updates_per_chain_bone) {
        const track = track_by_name.get(bone_name)
        if (track === undefined) continue
        const new_track = merge_rotation_updates(track, updates)
        const idx = clip.tracks.indexOf(track)
        if (idx >= 0) clip.tracks[idx] = new_track
        track_by_name.set(bone_name, new_track)
      }
    }

    return {
      clip: new AnimationClip(clip.name, clip.duration, clip.tracks),
      plant_phases_per_foot,
      frames_adjusted
    }
  }
}

/**
 * Apply each track's value at exactly `time` to the corresponding bone.
 * Searches each track's `times` array for an exact match; if none, finds the
 * surrounding pair and linearly interpolates (for quaternions: slerps).
 */
const TMP_QUAT_A = new Quaternion()
const TMP_QUAT_B = new Quaternion()
function apply_pose_at_time (skeleton: Skeleton, clip: AnimationClip, time: number): void {
  for (const track of clip.tracks) {
    const dot = track.name.indexOf('.')
    const bone_name = dot >= 0 ? track.name.slice(0, dot) : track.name
    const property = dot >= 0 ? track.name.slice(dot + 1) : ''
    const bone = skeleton.bones.find(b => b.name === bone_name)
    if (bone === undefined) continue
    const value = sample_track_at_time(track, time)
    if (value === null) continue
    if (property === 'quaternion' && value.length === 4) {
      bone.quaternion.set(value[0], value[1], value[2], value[3])
    } else if (property === 'position' && value.length === 3) {
      bone.position.set(value[0], value[1], value[2])
    }
  }
  // Recompute world matrices once after all bones updated.
  for (const bone of skeleton.bones) {
    if (bone.parent === null || !(bone.parent as Bone).isBone) {
      bone.updateMatrixWorld(true)
    }
  }
}

function sample_track_at_time (
  track: { times: ArrayLike<number>, values: ArrayLike<number>, getValueSize: () => number },
  time: number
): number[] | null {
  const size = track.getValueSize()
  const n = track.times.length
  if (n === 0) return null
  // Edge cases.
  if (time <= track.times[0]) {
    return read_value(track.values, 0, size)
  }
  if (time >= track.times[n - 1]) {
    return read_value(track.values, (n - 1) * size, size)
  }
  // Binary search for the surrounding pair.
  let lo = 0; let hi = n - 1
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1
    if (track.times[mid] <= time) lo = mid
    else hi = mid
  }
  const t0 = track.times[lo]
  const t1 = track.times[hi]
  const alpha = (time - t0) / (t1 - t0)
  const a = read_value(track.values, lo * size, size)
  const b = read_value(track.values, hi * size, size)
  if (size === 4) {
    // Quaternion: slerp.
    TMP_QUAT_A.set(a[0], a[1], a[2], a[3])
    TMP_QUAT_B.set(b[0], b[1], b[2], b[3])
    TMP_QUAT_A.slerp(TMP_QUAT_B, alpha)
    return [TMP_QUAT_A.x, TMP_QUAT_A.y, TMP_QUAT_A.z, TMP_QUAT_A.w]
  }
  // Vector / scalar: linear blend.
  const out: number[] = new Array(size)
  for (let i = 0; i < size; i++) out[i] = a[i] + (b[i] - a[i]) * alpha
  return out
}

function read_value (values: ArrayLike<number>, offset: number, size: number): number[] {
  const out: number[] = new Array(size)
  for (let i = 0; i < size; i++) out[i] = values[offset + i]
  return out
}

function detect_plant_phases (
  times: number[],
  foot_y_per_frame: number[],
  foot_world_per_frame: Vector3[],
  ground_threshold: number,
  velocity_threshold: number,
  min_plant_frames: number
): PlantPhase[] {
  const planted: boolean[] = new Array(times.length)
  for (let i = 0; i < times.length; i++) {
    const y = foot_y_per_frame[i]
    if (y > ground_threshold) { planted[i] = false; continue }
    // Compute |dy/dt| using neighbor frames.
    let velocity = 0
    if (i > 0 && i < times.length - 1) {
      velocity = Math.abs((foot_y_per_frame[i + 1] - foot_y_per_frame[i - 1]) / (times[i + 1] - times[i - 1]))
    }
    planted[i] = velocity < velocity_threshold
  }

  // Group contiguous planted runs.
  const phases: PlantPhase[] = []
  let run_start = -1
  for (let i = 0; i <= planted.length; i++) {
    const is_planted = i < planted.length && planted[i]
    if (is_planted && run_start < 0) {
      run_start = i
    } else if (!is_planted && run_start >= 0) {
      const run_end = i - 1
      const length = run_end - run_start + 1
      if (length >= min_plant_frames) {
        // Anchor = median X, Y, Z across the run (median is more robust than
        // mean against spike outliers in the first/last frame of a phase).
        const xs: number[] = []
        const ys: number[] = []
        const zs: number[] = []
        for (let k = run_start; k <= run_end; k++) {
          xs.push(foot_world_per_frame[k].x)
          ys.push(foot_world_per_frame[k].y)
          zs.push(foot_world_per_frame[k].z)
        }
        xs.sort((a, b) => a - b); ys.sort((a, b) => a - b); zs.sort((a, b) => a - b)
        const mid = Math.floor(xs.length / 2)
        phases.push({
          start_index: run_start,
          end_index: run_end,
          anchor_world: new Vector3(xs[mid], ys[mid], zs[mid])
        })
      }
      run_start = -1
    }
  }
  return phases
}

/**
 * Build a new QuaternionKeyframeTrack with selected times' values replaced
 * from `updates`. Times not in updates pass through unchanged.
 */
function merge_rotation_updates (
  original: QuaternionKeyframeTrack,
  updates: Map<number, [number, number, number, number]>
): QuaternionKeyframeTrack {
  const new_times: number[] = []
  const new_values: number[] = []
  for (let i = 0; i < original.times.length; i++) {
    const t = original.times[i]
    const update = updates.get(t)
    new_times.push(t)
    if (update !== undefined) {
      new_values.push(update[0], update[1], update[2], update[3])
    } else {
      new_values.push(
        original.values[i * 4],
        original.values[i * 4 + 1],
        original.values[i * 4 + 2],
        original.values[i * 4 + 3]
      )
    }
  }
  return new QuaternionKeyframeTrack(original.name, new_times, new_values)
}
